
import { DateTime } from 'luxon'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'

import { City } from '@/model/City'
import { Snapshot } from '@/model/Snapshot'
import { TimeStep } from '@/model/TimeStep'

import { ICityService } from '@/services/city/ICityService'
import { ISnapshotService } from '@/services/snapshot/ISnapshotService'

import OverviewMap from './map/OverviewMap.vue'
import RepresentationSelector from './representation-selector/RepresentationSelector.vue'
import Timeline from './timeline/Timeline.vue'
import CommandBar from '@/components/commands/CommandBar.vue'
import Disclaimer from './disclaimer/Disclaimer.vue'
import StationsTimeSeries from '@/components/station/StationsTimeSeries.vue'
import RainGaugesTimeSeries from '@/components/raingauge/RainGaugesTimeSeries.vue'
import { IModeService, ModeChangedEventArgs } from '@/services/mode/IModeService'
import { Mode } from '@/model/Mode'
import { Permissions } from '@/model/Permissions'
import { IAccessControlService } from '@/services/acl/IAccessControlService'
import { ITranslationService } from '@/services/translation/ITranslationService'
import { ICumulativeRainfallService } from '@/services/cumulative-rainfall/ICumulativeRainfallService'
import Legends from '@/components/legend/Legends.vue'
import { CumulativeRainfall } from '@/model/CumulativeRainFall'
import { IDateTimeService } from '@/services/datetime/IDateTimeService'
import { ISnapshotLike } from '@/model/ISnapshotLike'
import { FilteredSnapshot } from '@/model/FilteredSnapShot'
import {
  createCumulativeRainfallRepresentation,
  RegularRepresentation,
  regularRepresentations,
  Representation,
  RepresentationTypes,
} from '@/model/Representation'
import { getEnv } from '@/utils/env'
import { TimeStepCumulativeRainfall } from '@/model/TimeStepCumulativeRainfall'

const LIVE_MODE_REFRESH_RATE_IN_MILLIS = 30 * 1000
const PLAY_MODE_REFRESH_RATE_IN_MILLIS = (getEnv('VUE_APP_PLAY_INTERVAL') as number) || 1200

@Component({
  components: {
    Legends,
    OverviewMap,
    RepresentationSelector,
    CommandBar,
    Timeline,
    Disclaimer,
    StationsTimeSeries,
    RainGaugesTimeSeries,
  },
})
export default class Overview extends Vue {
  @Prop({ type: String, required: true }) cityId!: string

  @Prop(String) snapshotTimestamp!: string
  @Prop(String) timeStepTimestamp!: string

  private showStationRepresentationSelector = false

  private cityService: ICityService | null = null
  private snapshotService: ISnapshotService | null = null
  private modeService: IModeService | null = null
  private userService!: IAccessControlService
  private translationService!: ITranslationService
  private cumulativeRainfallService!: ICumulativeRainfallService
  private dateTimeService!: IDateTimeService
  private selectedRepresentation: RegularRepresentation = regularRepresentations['flood risk']
  private activeRepresentation: Representation<RepresentationTypes> = this.selectedRepresentation

  //those fields will allow downstream event propagation
  mouseMoveEvt: MouseEvent | null = null
  mouseClickEvt: MouseEvent | null = null

  private mode!: Mode
  private city: City | null = null
  private snapshot: ISnapshotLike<TimeStep> | null = null
  private timeStep: TimeStep | null = null

  private liveModeIntervalId: number | null = null
  private playModeIntervalId: number | null = null

  private hasAccess = false
  private customerHeader = ''

  enableMaxButton = true

  //
  // Component lifecycle
  //
  created(): void {
    this.cityService = this.$services.get<ICityService>('city')
    this.snapshotService = this.$services.get<ISnapshotService>('snapshot')
    this.modeService = this.$services.get<IModeService>('mode')
    this.userService = this.$services.get<IAccessControlService>('acl')
    this.translationService = this.$services.get<ITranslationService>('translation')
    this.cumulativeRainfallService = this.$services.get('cumulative-rainfall')
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.subscribe(
      this.enableMaxButtonOnCumulativeRainfall,
    )
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.subscribe(this.disableMaxMode)
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.subscribe(this.changeActiveRepresentation)

    this.dateTimeService = this.$services.get('datetime')
    this.initialize()
  }

  beforeDestroy(): void {
    this.modeService?.modeChanged.unsubscribe(this.onModeChanged)
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.unsubscribe(
      this.enableMaxButtonOnCumulativeRainfall,
    )
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.unsubscribe(this.disableMaxMode)
    this.cumulativeRainfallService.cumulativeRainfallSelectionChanged.unsubscribe(this.changeActiveRepresentation)

    this.enableMode(this.mode, false)
  }

  //
  // Mutators
  //

  setCity(cityId: string): void {
    if (this.city && this.city.getName() === cityId) {
      return
    }

    let city = this.cityService?.getCityCollection().getByName(cityId)
    if (!city) {
      return
    }

    this.city = city
  }

  setSnapshot(snapshot: ISnapshotLike<TimeStep>): void {
    this.snapshot = snapshot
  }

  private setSnapshotWithFilter(snapshot: Snapshot<TimeStep>): void {
    if (this.activeRepresentation.type === 'cumulative rainfall') {
      const cumMinutes = this.activeRepresentation.cumulativeRainfall.cumulativeTimeMinutes
      const aggregationMinutes = this.activeRepresentation.cumulativeRainfall.cumulAgregationTimeMinutes

      const filtered_snapshot = new FilteredSnapshot<TimeStep>(snapshot, (ts) => {
        if (ts instanceof TimeStepCumulativeRainfall) {
          return ts.hasCumulativeRainfallIntensityByRainfall(cumMinutes)
        }
        return false
      })
      // Set alignment interval so getStart()/getEnd() return aligned times for timeline
      filtered_snapshot.setAlignmentInterval(aggregationMinutes)
      this.setSnapshot(filtered_snapshot)
    } else {
      this.setSnapshot(snapshot)
    }
  }

  private findAlignedTimeStep(snapshot: ISnapshotLike<TimeStep>): TimeStep {
    // For non-cumulative rainfall or non-filtered snapshots, just use reference
    if (this.activeRepresentation.type !== 'cumulative rainfall' || !(snapshot instanceof FilteredSnapshot)) {
      return snapshot.getReference()
    }

    // For cumulative rainfall with FilteredSnapshot:
    // The reference is the most recent aligned timestep if it passes the filter
    // FilteredSnapshot.getReference() already returns an aligned timestep
    return snapshot.getReference()
  }

  setTimeStep(timeStep: TimeStep): void {
    this.timeStep = timeStep
  }

  //
  // Logic
  //
  initialize(): void {
    this.setCity(this.cityId)

    this.mode = this.modeService?.getMainMode() as Mode
    this.enableMode(this.mode, true)

    this.modeService?.modeChanged.subscribe(this.onModeChanged)
    this.setAccess()
    this.setCustomerHeader()
  }

  private setAccess(): void {
    this.hasAccess =
      this.userService.hasPermission(Permissions.HISTORY) || this.userService.hasPermission(Permissions.FULL_ACCESS)
  }

  private async setCustomerHeader(): Promise<void> {
    this.customerHeader = await this.translationService.getCustomerHeader()
  }

  reRoute(mode: Mode, snapshotTimestamp?: string, timeStepTimestamp?: string): void {
    const currentQuery = this.$route.query

    const query = { ...currentQuery }
    query['mode'] = mode
    if (snapshotTimestamp) {
      query['snapshotTimestamp'] = snapshotTimestamp
    } else {
      delete query['snapshotTimestamp']
    }
    if (timeStepTimestamp) {
      query['timeStepTimestamp'] = timeStepTimestamp
    } else {
      delete query['timeStepTimestamp']
    }

    const route = {
      name: 'Overview',
      params: {
        cityId: this.cityId,
      },
      query: query,
    }

    if (this.$route.fullPath === this.$router.resolve(route).route.fullPath) {
      return
    }

    this.$router.replace(route)
  }

  async enableMaxButtonOnCumulativeRainfall(cumulativeRainfall: CumulativeRainfall | undefined): Promise<void> {
    this.enableMaxButton = cumulativeRainfall === undefined
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async disableMaxMode(cumulativeRainfall: CumulativeRainfall | undefined): Promise<void> {
    if (this.mode === 'max') this.enableMode('max', false)
  }

  async loadSnapshot(city: City, timestamp: DateTime): Promise<Snapshot<TimeStep> | null> {
    let timestampUTC = timestamp
    if (timestampUTC.zoneName !== 'UTC') {
      timestampUTC = timestamp.toUTC()
    }
    const snapshot =
      this.activeRepresentation.type === 'cumulative rainfall'
        ? await this.snapshotService?.loadCumulativeRainfallSnapshot(
            city,
            this.dateTimeService.getUtcRounded(
              timestampUTC,
              this.activeRepresentation.cumulativeRainfall.cumulAgregationTimeMinutes,
            ),
          )
        : await this.snapshotService?.loadValueSnapshot(city, this.dateTimeService.getUtcRounded(timestampUTC))
    if (!snapshot) {
      return null
    }
    return snapshot
  }

  async changeActiveRepresentation(cumulativeRainfall: CumulativeRainfall | undefined): Promise<void> {
    if (!cumulativeRainfall) {
      this.activeRepresentation = this.selectedRepresentation
    } else {
      this.activeRepresentation = createCumulativeRainfallRepresentation(cumulativeRainfall)
    }
  }

  private enableMode(mode: Mode, enable: boolean): void {
    switch (mode) {
      case 'live':
        this.enableLiveMode(enable)
        break
      case 'free':
        this.enableFreeMode(enable)
        break
      case 'play':
        this.enablePlayMode(enable)
        break
      case 'pause':
        this.enablePauseMode(enable)
        break
      case 'max':
        this.enableMaxMode(enable)
        break
    }
  }

  private async enableLiveMode(enable: boolean): Promise<void> {
    if (enable) {
      this.liveModeIntervalId = setInterval(this.triggerLiveUpdate, LIVE_MODE_REFRESH_RATE_IN_MILLIS)
      await this.triggerLiveUpdate()
    } else {
      if (this.liveModeIntervalId) {
        window.clearInterval(this.liveModeIntervalId)
        this.liveModeIntervalId = null
      }
    }
  }

  private enableFreeMode(enable: boolean): void {
    if (enable) {
      return
    } else {
      return
    }
  }

  private async enablePlayMode(enable: boolean): Promise<void> {
    if (enable) {
      this.playModeIntervalId = window.setInterval(() => this.triggerPlayUpdate(), PLAY_MODE_REFRESH_RATE_IN_MILLIS)
      this.triggerPlayUpdate()
    } else {
      if (this.playModeIntervalId) {
        window.clearInterval(this.playModeIntervalId)
        this.playModeIntervalId = null
      }
    }
  }

  private enablePauseMode(enable: boolean): void {
    if (enable) {
      return
    } else {
      return
    }
  }

  private async enableMaxMode(enable: boolean): Promise<void> {
    if (enable) {
      if (this.snapshot) {
        const timeStepMax = this.snapshot.getMax()
        if (timeStepMax) {
          this.setTimeStep(timeStepMax)
        }
      }
    } else {
      if (this.snapshot) {
        this.setTimeStep(this.findAlignedTimeStep(this.snapshot))
      }
    }
  }

  private async triggerLiveUpdate(): Promise<void> {
    if (!this.city) {
      return
    }

    let requestTimestamp: DateTime

    if (this.activeRepresentation.type === 'cumulative rainfall') {
      // For cumulative rainfall, use the aggregation interval (e.g., 15 min for 1h, 60 min for 6h/24h)
      const aggregationInterval = this.activeRepresentation.cumulativeRainfall.cumulAgregationTimeMinutes
      requestTimestamp = this.dateTimeService.getUtcRounded(undefined, aggregationInterval)

      // Backend data has ~15 min delay from data provider processing
      // If we're within the first 15 minutes of a new interval, the latest aligned
      // timestep may not have data yet, so request the previous interval instead
      const now = DateTime.now().toUTC()
      const minutesSinceInterval = now.minute % aggregationInterval
      if (minutesSinceInterval < 15) {
        requestTimestamp = requestTimestamp.minus({ minutes: aggregationInterval })
      }
    } else {
      requestTimestamp = this.dateTimeService.getUtcRounded()
    }

    const snapshot = await this.loadSnapshot(this.city, requestTimestamp)
    if (!snapshot) {
      return
    }

    this.setSnapshotWithFilter(snapshot)
  }

  private triggerPlayUpdate(): void {
    if (this.mode === 'play' && this.snapshot && this.timeStep) {
      if (this.timeStep.isMax()) {
        this.setTimeStep(this.findAlignedTimeStep(this.snapshot))
        return
      }
      const nextTimeStep = this.snapshot.nextTimeStep(this.timeStep.timestamp.toUTC())
      if (nextTimeStep) {
        this.setTimeStep(nextTimeStep)
      }
    }
  }

  showStationRepresentation(hasStations: boolean): void {
    this.showStationRepresentationSelector = hasStations
  }

  //
  // Watch URL Parameters
  //
  @Watch('cityId')
  onCityIdChanged(cityId: string): void {
    this.setCity(cityId)
  }

  @Watch('snapshotTimestamp')
  async onSnapshotTimestampChanged(timestamp: string): Promise<void> {
    if (!this.city || !timestamp) {
      return
    }

    const dateTime = DateTime.fromISO(timestamp, { zone: 'UTC' })
    if (!dateTime.isValid) {
      return
    }

    const snapshot = await this.loadSnapshot(this.city, dateTime)
    if (!snapshot) {
      return
    }

    this.setSnapshotWithFilter(snapshot)
  }

  @Watch('timeStepTimestamp')
  onTimeStepTimestampChanged(timestamp: string): void {
    if (!this.city || !this.snapshot) {
      return
    }

    if (this.mode === 'max') {
      return
    }

    const timestampUTC = DateTime.fromISO(timestamp, { zone: 'UTC' })
    let timeStep = this.snapshot.findTimeStepAt(timestampUTC)
    this.setTimeStep(timeStep ? timeStep : this.snapshot.getReference())
  }

  //
  // Watch attributes
  //
  @Watch('city')
  async onCityChanged(city: City): Promise<void> {
    let snapshotTimestamp = null

    if (this.snapshot) {
      snapshotTimestamp = this.snapshot.timestamp
    } else if (this.snapshotTimestamp) {
      const timestamp = DateTime.fromISO(this.snapshotTimestamp, { zone: 'UTC' })
      snapshotTimestamp = timestamp.isValid ? timestamp : this.dateTimeService.getUtcRounded()
    } else {
      snapshotTimestamp = this.dateTimeService.getUtcRounded()
    }

    const snapshot = await this.loadSnapshot(city, snapshotTimestamp)
    if (snapshot) {
      this.setSnapshotWithFilter(snapshot)
    }
  }

  @Watch('selectedRepresentation')
  onRepresentationChange(representation: RegularRepresentation): void {
    this.activeRepresentation = representation
  }

  @Watch('activeRepresentation')
  async onActiveRepresentationChange(
    newR: Representation<RepresentationTypes>,
    oldR: Representation<RepresentationTypes>,
  ): Promise<void> {
    const switchingToCumulative = newR.type === 'cumulative rainfall'
    const switchingFromCumulative = newR.type !== 'cumulative rainfall' && oldR.type === 'cumulative rainfall'
    const switchingBetweenCumulative = newR.type === 'cumulative rainfall' && oldR.type === 'cumulative rainfall'

    if ((switchingToCumulative || switchingFromCumulative || switchingBetweenCumulative) && this.city && this.timeStep) {
      let requestTimestamp: DateTime

      // In live mode, use current time; in historical/free mode, preserve the current timestamp
      const baseTimestamp = this.mode === 'live' ? undefined : this.timeStep.timestamp

      if (switchingToCumulative || switchingBetweenCumulative) {
        // For cumulative rainfall, use the aggregation interval of the NEW representation
        const aggregationInterval = (newR as Representation<'cumulative rainfall'>).cumulativeRainfall
          .cumulAgregationTimeMinutes
        requestTimestamp = this.dateTimeService.getUtcRounded(baseTimestamp, aggregationInterval)
      } else {
        // Switching from cumulative to intensity
        requestTimestamp = this.dateTimeService.getUtcRounded(baseTimestamp)
      }

      const snapshot = await this.loadSnapshot(this.city, requestTimestamp)
      if (snapshot) {
        this.setSnapshotWithFilter(snapshot)
      }
    }
  }

  @Watch('snapshot')
  onSnapshotChanged(snapshot: Snapshot<TimeStep>): void {
    const timeStepMax = snapshot.getMax()
    if (this.mode === 'max' && timeStepMax) {
      this.setTimeStep(timeStepMax)
      return
    }

    if (!this.timeStep) {
      this.setTimeStep(this.findAlignedTimeStep(snapshot))
      return
    }

    if (this.timeStep.snapshot.city === this.city) {
      this.setTimeStep(this.findAlignedTimeStep(snapshot))
      return
    }

    const timeStep = snapshot.findTimeStepAt(this.timeStep.timestamp)
    this.setTimeStep(timeStep ? timeStep : this.findAlignedTimeStep(snapshot))
  }

  async onModeChanged(args: ModeChangedEventArgs): Promise<void> {
    if (args.oldMainMode !== args.newMainMode) {
      this.enableMode(this.mode, false)
      this.enableMode('pause', true)
      this.mode = args.newMainMode
    } else {
      this.enableMode(this.mode, false)
      this.mode = args.newSecondaryMode
    }

    this.enableMode(this.mode, true)
    this.reRouteOnModeChanged(this.mode)
  }

  reRouteOnModeChanged(mode: Mode): void {
    const fullReRouteModes = ['free', 'play', 'pause']

    if (fullReRouteModes.indexOf(mode) !== -1) {
      this.reRoute(mode, this.snapshotTimestamp, this.timeStepTimestamp)
    } else {
      this.reRoute(mode)
    }
  }

  //
  // Mouse events
  //
  onMouseMove(evt: MouseEvent): void {
    this.mouseMoveEvt = evt
  }

  onMouseClick(evt: MouseEvent): void {
    this.mouseClickEvt = evt
  }

  //
  // Timeline events
  //
  onTimelineSnapshotChanged(timestamp: DateTime): void {
    this.reRoute('free', timestamp.toUTC().toISO(), timestamp.toUTC().toISO())
  }

  onTimelineTimeStepChanged(timeStep: TimeStep): void {
    this.reRoute(this.mode, this.snapshotTimestamp, timeStep.timestamp.toUTC().toISO())
  }
}
